import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Sun, Droplets, Sprout, Ruler, Calendar, Scissors, MapPin, BookOpen, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import Seo from "../components/Seo";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4 py-4 border-b border-darkRoast/10">
    <Icon className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" strokeWidth={1.5} />
    <div className="flex-1">
      <div className="text-[10px] tracking-wider-2 uppercase text-earth mb-1">{label}</div>
      <div className="text-darkRoast leading-relaxed">{value}</div>
    </div>
  </div>
);

export default function GrowGuideDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/grow-guides/${slug}`).then(({ data }) => {
      setP(data);
    }).catch(() => setP(false));
  }, [slug]);

  if (p === null) return <div className="max-w-7xl mx-auto px-6 py-20 text-earth font-display text-3xl">Loading…</div>;
  if (p === false) return <div className="max-w-7xl mx-auto px-6 py-20 text-earth">Guide not found.</div>;

  const g = p.grow_guide || {};
  const shortName = g.variety_title || p.name;
  const seoDescription = `How to grow ${shortName} in California's Sierra foothills: sun, soil, spacing, watering and harvest tips. Naturally sourced, non-GMO ${p.category.toLowerCase()} seeds from Amador County.`.slice(0, 158);
  const guideJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `How to Grow ${shortName}`,
    description: seoDescription,
    image: p.image_url ? [p.image_url] : undefined,
    articleSection: p.category,
    author: { "@type": "Organization", name: "Rooted in Amador" },
    publisher: {
      "@type": "Organization",
      name: "Rooted in Amador",
      logo: { "@type": "ImageObject", url: "https://images.unsplash.com/photo-1466637574441-749b8f19452f?auto=format&fit=crop&w=600&q=80" },
    },
    ...(g.sources && g.sources.length
      ? { citation: g.sources.map((s) => ({ "@type": "CreativeWork", name: s.title, url: s.url })) }
      : {}),
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="grow-guide-detail">
      <Seo
        title={`How to Grow ${shortName}`}
        description={seoDescription}
        path={`/grow/${slug}`}
        image={p.image_url}
        type="article"
        jsonLd={guideJsonLd}
      />
      <Link to="/grow" className="inline-flex items-center gap-2 text-xs tracking-wider-2 uppercase text-earth hover:text-darkRoast mb-10">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All grow guides
      </Link>

      <article>
        <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">{p.category} · Growing Guide</div>
        <h1 className="font-display text-5xl sm:text-6xl text-darkRoast leading-[1.05] max-w-3xl" data-testid="guide-title">
          How to Grow {shortName}
        </h1>
        <p className="mt-6 text-earth leading-relaxed max-w-2xl">
          A practical growing guide for <em className="not-italic font-medium text-darkRoast">{p.name}</em>,
          written for gardeners in California's Sierra foothills.
          {g.maturity && <> Expect first harvest in around <strong className="text-darkRoast font-medium">{g.maturity}</strong> from sowing.</>}
        </p>

        <div className="my-12 aspect-[21/9] overflow-hidden rounded-sm border border-darkRoast/10 bg-surface">
          {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
        </div>

        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-display text-3xl text-darkRoast mb-2">At a glance</h2>
            <div className="mt-2">
              {g.sun && <Row icon={Sun} label="Sun" value={g.sun} />}
              {g.soil && <Row icon={Sprout} label="Soil" value={g.soil} />}
              {g.depth && <Row icon={Ruler} label="Planting depth" value={g.depth} />}
              {g.spacing && <Row icon={Ruler} label="Spacing" value={g.spacing} />}
              {g.germinate && <Row icon={Calendar} label="Days to germinate" value={g.germinate} />}
              {g.maturity && <Row icon={Calendar} label="Days to maturity" value={g.maturity} />}
              {g.water && <Row icon={Droplets} label="Watering" value={g.water} />}
              {g.when && <Row icon={Calendar} label="When to plant" value={g.when} />}
              {g.harvest && <Row icon={Scissors} label="Harvest" value={g.harvest} />}
            </div>

            {g.amador_tip && (
              <div className="mt-10 bg-sage/10 border-l-4 border-sage p-6 rounded-sm">
                <div className="flex items-center gap-2 text-xs tracking-wider-2 uppercase text-sage mb-2">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} /> Amador tip
                </div>
                <p className="text-darkRoast leading-relaxed">{g.amador_tip}</p>
              </div>
            )}

            <h2 className="font-display text-3xl text-darkRoast mt-14 mb-4">Why grow your own</h2>
            <p className="text-earth leading-relaxed">
              Growing {shortName.toLowerCase()} from seed is one of the most rewarding ways to
              skip the pesticide-treated, ultra-shipped produce in conventional grocery stores. You
              control the soil, the water, and the timing, and at the end of the season you can save
              seed to plant again next year. That's how gardens used to work.
            </p>
            <p className="text-earth leading-relaxed mt-4">
              Our packets are naturally sourced, non-GMO, and hand-packed here in Amador County so
              they arrive ready to plant.
            </p>

            {g.sources && g.sources.length > 0 && (
              <div className="mt-14 pt-8 border-t border-darkRoast/10" data-testid="guide-sources">
                <div className="flex items-center gap-2 text-xs tracking-wider-2 uppercase text-sage mb-4">
                  <BookOpen className="w-4 h-4" strokeWidth={1.5} /> Sources & further reading
                </div>
                <p className="text-sm text-earth mb-4 leading-relaxed">
                  These growing recommendations are compiled from publicly available, university-extension
                  and trusted horticultural references. Verify any guidance against your own local conditions.
                </p>
                <ul className="space-y-2">
                  {g.sources.map((s, i) => (
                    <li key={i}>
                      <a href={s.url} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-2 text-darkRoast hover:text-sage underline underline-offset-4 text-sm"
                         data-testid={`source-link-${i}`}>
                        {s.title}
                        <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar: buy CTA */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 bg-surface p-6 rounded-sm">
              <div className="aspect-square overflow-hidden bg-cream border border-darkRoast/10 rounded-sm mb-5">
                {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
              </div>
              <div className="text-[10px] tracking-wider-2 uppercase text-sage mb-1">{p.category}</div>
              <h3 className="font-display text-xl text-darkRoast leading-tight">{p.name}</h3>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="font-display text-2xl text-darkRoast">${p.price.toFixed(2)}</span>
                <span className="text-sm text-earth">/ {p.packet_size_g}g</span>
              </div>
              <button
                onClick={() => { addItem(p, 1); toast.success("Added to cart"); }}
                className="mt-5 w-full bg-darkRoast text-cream py-3 rounded-full text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors flex items-center justify-center gap-2"
                data-testid="guide-add-to-cart"
              >
                <ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> Add to cart
              </button>
              <Link
                to={`/product/${p.product_id}`}
                className="block text-center mt-3 text-xs tracking-wider-2 uppercase text-earth hover:text-darkRoast"
              >
                View product
              </Link>
            </div>
          </aside>
        </div>
      </article>
    </div>
  );
}
